import { InputBase, type SxProps, type Theme } from "@mui/material";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type FocusEvent,
  type KeyboardEvent,
} from "react";
import {
  focusGridCell,
  focusPlanEditorLastCell,
  setLastTabDirection,
} from "@platform/btx-grid-components";

import { formatForDisplay } from "../../../utils/rateFormatters";

const getGridNavTarget = (
  key: string,
  shiftKey: boolean,
  cellIndex: number,
  columnsInRow: number,
): { targetIndex: number | null; tabDirection?: "forward" | "backward" } => {
  switch (key) {
    case "Tab":
      return {
        targetIndex: shiftKey ? cellIndex - 1 : cellIndex + 1,
        tabDirection: shiftKey ? "backward" : "forward",
      };
    case "Enter":
      return {
        targetIndex: shiftKey
          ? cellIndex - columnsInRow
          : cellIndex + columnsInRow,
      };
    case "ArrowDown":
      return { targetIndex: cellIndex + columnsInRow };
    case "ArrowUp":
      return { targetIndex: cellIndex - columnsInRow };
    default:
      return { targetIndex: null };
  }
};

const isCollapsedSelection = (input: HTMLInputElement): boolean =>
  input.selectionStart !== null &&
  input.selectionEnd !== null &&
  input.selectionStart === input.selectionEnd;

const handleHorizontalGridNavigation = (
  event: React.KeyboardEvent<HTMLInputElement>,
  gridId: string,
  cellIndex: number,
): boolean => {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
    return false;
  }

  if (!isCollapsedSelection(event.currentTarget)) {
    return true;
  }

  const { selectionStart, value } = event.currentTarget;
  const atStart = selectionStart === 0;
  const atEnd = selectionStart === value.length;

  const targetIndex =
    event.key === "ArrowLeft" && atStart
      ? cellIndex - 1
      : event.key === "ArrowRight" && atEnd
        ? cellIndex + 1
        : null;

  if (targetIndex === null || targetIndex < 0) {
    return true;
  }

  if (focusGridCell(gridId, targetIndex)) {
    event.preventDefault();
  }

  return true;
};

const handleTabGridNavigation = (
  event: React.KeyboardEvent<HTMLInputElement>,
  cellIndex: number,
): boolean => {
  if (event.key !== "Tab") {
    return false;
  }

  if (event.shiftKey && cellIndex === 0) {
    setLastTabDirection("backward");
    if (focusPlanEditorLastCell()) {
      event.preventDefault();
    }
    return true;
  }

  return false;
};

const handleDirectionalGridNavigation = (
  event: React.KeyboardEvent<HTMLInputElement>,
  cellIndex: number,
  columnsInRow: number,
  gridId: string,
): void => {
  const { targetIndex, tabDirection } = getGridNavTarget(
    event.key,
    event.shiftKey,
    cellIndex,
    columnsInRow,
  );

  if (tabDirection) {
    setLastTabDirection(tabDirection);
  }

  if (targetIndex !== null && targetIndex >= 0) {
    const moved = focusGridCell(gridId, targetIndex);
    if (moved) {
      event.preventDefault();
    }
  }
};

export interface NumericInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  sx?: SxProps<Theme>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** Allow decimal values (default: true for rates, can be set to false for integer fields like enrolled lives) */
  allowDecimals?: boolean;
  /** Allow negative values (default: false) */
  allowNegative?: boolean;
  /** Make the input read-only */
  readOnly?: boolean;
  disabled?: boolean;
  /**
   * Prefix the displayed value with "$" for dollar amounts.
   * The prefix is shown only while the input is idle; the raw number is shown
   * while editing so typing and validation are unaffected. (default: false)
   */
  isCurrency?: boolean;
  /**
   * Group the integer part with thousands separators (1,000) while the input is
   * idle. Like the currency prefix, grouping is dropped while editing.
   * (default: true)
   */
  groupThousands?: boolean;
}

export interface DebouncedNumericInputProps extends Omit<
  NumericInputProps,
  "value" | "onChange"
> {
  /**
   * Server/external value that should be displayed when the input is not being edited.
   * This value is only applied when the input is not focused (not dirty).
   */
  serverValue: string;
  /**
   * Called when the user finishes editing (on blur) with the final cleaned value.
   * Use this to trigger your save/mutation.
   */
  onCommit: (value: string) => void;
  /**
   * Grid navigation: unique identifier for the grid (allows multiple grids on same page)
   */
  gridId?: string;
  /**
   * Grid navigation: zero-based index of this cell in the grid (left-to-right, top-to-bottom)
   */
  cellIndex?: number;
  /**
   * Grid navigation: number of navigable columns in each row
   * Used to calculate vertical navigation (Enter/ArrowDown moves by this amount)
   */
  columnsInRow?: number;
}

/**
 * Regex patterns for numeric validation
 */
const PATTERNS = {
  // Allows: digits, one decimal point, and optionally a leading minus
  decimalWithNegative: /^-?\d*\.?\d*$/,
  // Allows: digits and one decimal point
  decimalPositive: /^-?\d*\.?\d*$/.source.replace("-?", ""),
  // Allows: digits and optionally a leading minus
  integerWithNegative: /^-?\d*$/,
  // Allows: only digits
  integerPositive: /^\d*$/,
};

/**
 * Get the appropriate validation regex based on options
 */
const getValidationPattern = (
  allowDecimals: boolean,
  allowNegative: boolean,
): RegExp => {
  if (allowDecimals && allowNegative) {
    return PATTERNS.decimalWithNegative;
  }
  if (allowDecimals && !allowNegative) {
    return /^\d*\.?\d*$/;
  }
  if (!allowDecimals && allowNegative) {
    return PATTERNS.integerWithNegative;
  }
  return PATTERNS.integerPositive;
};

/**
 * NumericInput - A text input that validates and only allows numeric input
 *
 * This component provides better UX than type="number" inputs:
 * - No awkward spinner buttons
 * - Better mobile keyboard experience (uses inputMode)
 * - Prevents non-numeric input
 * - Cleans up values on blur
 * - Supports both integer and decimal modes
 *
 * @example
 * // For rate values (decimals allowed)
 * <NumericInput
 *   value={rateValue}
 *   onChange={setRateValue}
 *   onBlur={handleSave}
 *   allowDecimals
 * />
 *
 * // For enrolled lives (integers only)
 * <NumericInput
 *   value={enrolledLives}
 *   onChange={setEnrolledLives}
 *   onBlur={handleSave}
 *   allowDecimals={false}
 * />
 */
export const NumericInput: FC<NumericInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "-",
  sx,
  inputProps = {},
  allowDecimals = true,
  allowNegative = false,
  readOnly = false,
  disabled = false,
  isCurrency = false,
  groupThousands = true,
}) => {
  const validationPattern = getValidationPattern(allowDecimals, allowNegative);

  // Show the formatted value ("$1,000") while idle, and the raw value ("1000")
  // while editing so typing and validation operate on plain digits.
  const [isFocused, setIsFocused] = useState(false);
  const displayValue = isFocused
    ? value
    : formatForDisplay(value, isCurrency, groupThousands);

  /**
   * Handle input changes - validate and filter non-numeric characters
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      // Allow empty value
      if (newValue === "") {
        onChange("");
        return;
      }

      // Allow just a minus sign when negative numbers are permitted
      if (allowNegative && newValue === "-") {
        onChange("-");
        return;
      }

      // Allow just a decimal point for easier entry (will be cleaned on blur)
      if (allowDecimals && (newValue === "." || newValue === "-.")) {
        onChange(newValue);
        return;
      }

      // Test if the new value matches our validation pattern
      if (validationPattern.test(newValue)) {
        onChange(newValue);
      }
      // If not valid, don't update (keeps previous value)
    },
    [onChange, allowDecimals, allowNegative, validationPattern],
  );

  /**
   * Handle blur - clean up the value and trigger save
   */
  const handleBlur = useCallback(
    (_e: FocusEvent<HTMLInputElement>) => {
      // Leaving edit mode: switch the display back to the formatted value.
      setIsFocused(false);

      // Clean up partial values like "." or "-" or "-."
      let cleanedValue = value;

      if (
        cleanedValue === "." ||
        cleanedValue === "-" ||
        cleanedValue === "-."
      ) {
        cleanedValue = "";
      }

      // Remove trailing decimal point
      if (cleanedValue.endsWith(".")) {
        cleanedValue = cleanedValue.slice(0, -1);
      }

      // Remove leading zeros (but keep "0" and "0.xxx")
      if (
        cleanedValue.length > 1 &&
        cleanedValue.startsWith("0") &&
        !cleanedValue.startsWith("0.")
      ) {
        cleanedValue = cleanedValue.replace(/^0+/, "") || "0";
      }
      if (
        cleanedValue.startsWith("-0") &&
        cleanedValue.length > 2 &&
        !cleanedValue.startsWith("-0.")
      ) {
        cleanedValue = `-${cleanedValue.slice(2).replace(/^0+/, "") || "0"}`;
      }

      // Update if value was cleaned
      if (cleanedValue !== value) {
        onChange(cleanedValue);
      }

      // Trigger the onBlur callback for saving
      onBlur?.();
    },
    [value, onChange, onBlur],
  );

  /**
   * Handle key down - prevent certain keys and allow navigation
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, arrows
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];

      if (allowedKeys.includes(e.key)) {
        return;
      }

      // Allow Ctrl/Cmd + A, C, V, X
      if (
        (e.ctrlKey || e.metaKey) &&
        ["a", "c", "v", "x"].includes(e.key.toLowerCase())
      ) {
        return;
      }

      // Allow digits
      if (/^\d$/.test(e.key)) {
        return;
      }

      // Allow decimal point if decimals are allowed
      if (allowDecimals && e.key === ".") {
        return;
      }

      // Allow minus sign at the beginning if negative is allowed
      if (allowNegative && e.key === "-") {
        return;
      }

      // Block all other keys
      e.preventDefault();
    },
    [allowDecimals, allowNegative],
  );

  return (
    <InputBase
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      sx={sx}
      inputProps={{
        // Use decimal inputMode for rates, numeric for integers
        inputMode: allowDecimals ? "decimal" : "numeric",
        // Provide pattern for HTML5 validation (not enforced, just hints)
        pattern: allowDecimals ? "[0-9]*\\.?[0-9]*" : "[0-9]*",
        autoComplete: "off",
        ...inputProps,
        onFocus: (e: FocusEvent<HTMLInputElement>) => {
          // Entering edit mode: reveal the raw value before selecting it.
          // Read-only fields (e.g. computed totals) aren't editable, so keep
          // the formatted value while still allowing select/copy.
          const target = e.currentTarget;
          if (!readOnly) {
            setIsFocused(true);
          }
          requestAnimationFrame(() => {
            target.select();
            inputProps.onFocus?.(e);
          });
        },
      }}
    />
  );
};

/**
 * Clean up a value for display/commit (remove trailing decimals, leading zeros, etc.)
 * @internal Exported for testing
 */
export const cleanValueForCommit = (value: string): string => {
  let cleaned = value;

  // Clean up partial values like "." or "-" or "-."
  if (cleaned === "." || cleaned === "-" || cleaned === "-.") {
    return "";
  }

  // Remove trailing decimal point
  if (cleaned.endsWith(".")) {
    cleaned = cleaned.slice(0, -1);
  }

  // Remove leading zeros (but keep "0" and "0.xxx")
  if (
    cleaned.length > 1 &&
    cleaned.startsWith("0") &&
    !cleaned.startsWith("0.")
  ) {
    cleaned = cleaned.replace(/^0+/, "") || "0";
  }
  if (
    cleaned.startsWith("-0") &&
    cleaned.length > 2 &&
    !cleaned.startsWith("-0.")
  ) {
    cleaned = `-${cleaned.slice(2).replace(/^0+/, "") || "0"}`;
  }

  return cleaned;
};

/**
 * DebouncedNumericInput - A numeric input that maintains local state while editing
 *
 * This component solves the "race condition" problem where:
 * 1. User edits Input A, then quickly clicks Input B
 * 2. Blur on Input A triggers a server request
 * 3. User starts typing in Input B
 * 4. Server response comes back and would normally overwrite Input B's value
 *
 * Solution: Track pending commits. While a commit is in flight, sync from the
 * server only once its value moves off the baseline we committed away from
 * (the server acknowledged our edit). A refetch that re-delivers the pre-edit
 * value — e.g. an invalidate-driven refetch that lands before our own save
 * persists — is ignored so it can't reset the user's in-flight edit.
 *
 * The user's last entered value is always preserved until the server responds.
 *
 * Grid Navigation:
 * - Tab: Move to next cell (index + 1)
 * - Shift+Tab: Move to previous cell (index - 1)
 * - Enter/ArrowDown: Move down one row (index + columnsInRow)
 * - Shift+Enter/ArrowUp: Move up one row (index - columnsInRow)
 * - ArrowLeft/ArrowRight: Move to adjacent cells when the caret is at the
 *   start or end of the current value
 *
 * @example
 * <DebouncedNumericInput
 *   serverValue={rate.rate != null ? String(rate.rate) : ""}
 *   onCommit={(value) => {
 *     const numericValue = parseFloat(value);
 *     if (!isNaN(numericValue)) {
 *       updateRate({ rate: numericValue });
 *     }
 *   }}
 *   allowDecimals
 *   gridId="age-banded-gender"
 *   cellIndex={0}
 *   columnsInRow={4}
 * />
 */
export const DebouncedNumericInput: FC<DebouncedNumericInputProps> = ({
  serverValue,
  onCommit,
  onBlur,
  placeholder = "-",
  sx,
  inputProps = {},
  allowDecimals = true,
  allowNegative = false,
  readOnly = false,
  disabled = false,
  isCurrency = false,
  groupThousands = true,
  gridId,
  cellIndex,
  columnsInRow,
}) => {
  // Local state for the input value
  const [localValue, setLocalValue] = useState(serverValue);

  // Ref to track if we're focused
  const isFocusedRef = useRef(false);

  // Track pending commit value - the value we sent to the server but haven't received confirmation for
  const pendingCommitRef = useRef<string | null>(null);

  // While a commit is in flight, the server value we committed *away from*.
  // Used to distinguish the server acknowledging our edit (value moves off this
  // baseline) from a stale/echo refetch that re-delivers the pre-edit value.
  const committedFromRef = useRef<string | null>(null);

  // Check if grid navigation is enabled
  const hasGridNav =
    gridId != null && cellIndex != null && columnsInRow != null;

  // Sync server value to local state when appropriate
  useEffect(() => {
    // If a commit is in flight and the server has moved off the value we
    // committed away from, the server has processed our edit (or an
    // authoritative newer value arrived). Clear the in-flight guard — even
    // while focused — so a later legitimate change back to the baseline isn't
    // mistaken for a stale echo and ignored.
    if (
      pendingCommitRef.current !== null &&
      serverValue !== committedFromRef.current
    ) {
      pendingCommitRef.current = null;
      committedFromRef.current = null;
    }

    // Never overwrite what the user is actively typing.
    if (isFocusedRef.current) {
      return;
    }

    // A commit is still pending and the server still reports the value we
    // committed away from: a stale or unrelated refetch (e.g. an
    // invalidate-driven refetch that landed before our own save persisted).
    // Keep showing the user's in-flight value rather than snapping back.
    if (pendingCommitRef.current !== null) {
      return;
    }

    setLocalValue(serverValue);
  }, [serverValue]);

  // Handle focus
  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  // Handle local value changes
  const handleChange = useCallback((newValue: string) => {
    setLocalValue(newValue);
  }, []);

  // Handle blur - clean value and commit
  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;

    const cleanedValue = cleanValueForCommit(localValue);

    // Update local state with cleaned value
    if (cleanedValue !== localValue) {
      setLocalValue(cleanedValue);
    }

    // Only commit if value actually changed from server value
    if (cleanedValue !== serverValue) {
      // Mark this value as pending until the server responds, and remember the
      // value we're replacing so a stale refetch can't reset the edit.
      pendingCommitRef.current = cleanedValue;
      committedFromRef.current = serverValue;
      onCommit(cleanedValue);
    }

    // Call the optional onBlur callback
    onBlur?.();
  }, [localValue, serverValue, onCommit, onBlur]);

  // Handle grid navigation key events
  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!hasGridNav) {
        return;
      }

      if (handleHorizontalGridNavigation(e, gridId!, cellIndex!)) {
        return;
      }

      if (handleTabGridNavigation(e, cellIndex!)) {
        return;
      }

      handleDirectionalGridNavigation(e, cellIndex!, columnsInRow!, gridId!);
    },
    [hasGridNav, gridId, cellIndex, columnsInRow],
  );

  // Build data attribute for grid navigation
  const gridNavAttribute = hasGridNav
    ? { "data-grid-nav": `${gridId}-${cellIndex}` }
    : {};

  return (
    <NumericInput
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      sx={sx}
      inputProps={{
        onFocus: handleFocus,
        onKeyDown: handleGridKeyDown,
        ...gridNavAttribute,
        ...inputProps,
      }}
      allowDecimals={allowDecimals}
      allowNegative={allowNegative}
      readOnly={readOnly}
      disabled={disabled}
      isCurrency={isCurrency}
      groupThousands={groupThousands}
    />
  );
};
